import { useState } from 'react';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { formatDateTime } from '../../utils/dateFormat';
import { Copy, Download } from 'lucide-react';

interface RecoveryCodesDisplayProps {
  codes: string[];
  showWarning?: boolean;
}

export function RecoveryCodesDisplay({ codes, showWarning = true }: RecoveryCodesDisplayProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const codesText = codes.join('\n');
    await navigator.clipboard.writeText(codesText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const codesText = `SEQUESTO OS Recovery Codes\n\nSave these codes in a secure location. Each code can only be used once.\n\n${codes.join('\n')}\n\nGenerated on: ${formatDateTime(new Date())}`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sequesto-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {showWarning && (
        <Alert variant="warning">
          Save these recovery codes in a secure place. They can be used to access your account if you lose your authenticator device. Each code can only be used once.
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-2 bg-surface-secondary p-4 rounded-lg">
        {codes.map((code, index) => (
          <code
            key={index}
            className="text-body-sm font-mono text-text-primary bg-surface-primary px-3 py-2 rounded text-center"
          >
            {code}
          </code>
        ))}
      </div>

      <div className="flex gap-3">
        <Button variant="outline" size="sm" onClick={handleCopy} className="flex-1">
          <Copy className="h-4 w-4 mr-2" />
          {copied ? 'Copied!' : 'Copy all'}
        </Button>
        <Button variant="outline" size="sm" onClick={handleDownload} className="flex-1">
          <Download className="h-4 w-4 mr-2" />
          Download
        </Button>
      </div>
    </div>
  );
}
